import { defineStore } from "pinia";
import { createCookie, eraseCookie, setModalVisibility } from "../helpers/helpers";

const currencyCodeToSymbol = {
    USD: "$",
    EUR: "€",
    BRL: "R$",
    PLN: "zł",
    CAD: "C$",
    NOK: "kr",
    GBP: "£",
};

export const useCallsStore = defineStore("calls", {
    state: () => ({
        locationObject: null,
        gameUri: null,
        currentGame: null,
        userObject: null,
        realBalanceObject: null,
        bonusBalanceObject: null,
        virtualBalanceObject: null,
        paymentConnectionsArray: [],
        gameSessionConnectionArray: [],
        gameRoundsConnectionArray: [],
        limitsArray: [],
        documentsArray: [],
        bonusesArray: [],
        depositBonusesArray: [],
        depositPaymentOptions: [],
        withdrawPaymentOptions: [],
        paymentInfoList: [],
        userCampaignsList: [],
        vipProgressObject: null,
        liveGameStatsArray: [],
        recentWinningsArray: [],
    }),
    actions: {
        // NON PERSISTED CALLS
        setLocationObject(newValue) {
            this.locationObject = Object.assign({}, newValue);
        },
        setGameUri(newValue, game, router, myStore) {
            if (game) {
                createCookie("gameId", game.gameId, 180);
                this.currentGame = Object.assign({}, game);
                router.push("/game");
                setTimeout(function () {
                    // When a game is launched from the /game route the watch on the route in the home.vue will not be triggered (Because we are already in /game)
                    // So I need to set the modal visibility manually
                    setModalVisibility(true);
                    myStore.setDisabledGameThumbnails(false);
                }, 100);
            } else {
                eraseCookie("gameId");
                this.currentGame = null;
                setModalVisibility(false, router);
            }
            this.gameUri = newValue;
        },
        setUserObject(newValue) {
            this.userObject = newValue ? Object.assign({}, newValue) : null;
        },
        setBalanceObjects(newValue) {
            if (newValue === null) {
                this.realBalanceObject = null;
                this.bonusBalanceObject = null;
            } else if (this.userObject && this.userObject.currencyCode) {
                newValue.map((balance) => {
                    if (balance.type === "REAL" && balance.currencyCode === this.userObject.currencyCode) {
                        this.realBalanceObject = Object.assign({}, this.realBalanceObject, balance);
                    } else if (balance.type === "BONUS" && balance.currencyCode === this.userObject.currencyCode) {
                        this.bonusBalanceObject = Object.assign({}, this.bonusBalanceObject, balance);
                    }
                    else if (balance.type === "VIRTUAL") {
                        this.virtualBalanceObject = Object.assign({}, this.virtualBalanceObject, balance);
                    }
                });
            } else {
                newValue.map((balance) => {
                    if (balance.type === "REAL") {
                        this.realBalanceObject = Object.assign({}, this.realBalanceObject, balance);
                    } else if (balance.type === "BONUS") {
                        this.bonusBalanceObject = Object.assign({}, this.bonusBalanceObject, balance);
                    }
                    else if (balance.type === "VIRTUAL") {
                        this.virtualBalanceObject = Object.assign({}, this.virtualBalanceObject, balance);
                    }
                });
            }
        },
        setPaymentConnectionsArray(newValue) {
            this.paymentConnectionsArray = [...newValue];
        },
        setGameSessionConnectionArray(newValue) {
            this.gameSessionConnectionArray = [...newValue];
        },
        setGameRoundsConnectionArray(newValue) {
            this.gameRoundsConnectionArray = [...newValue];
        },
        setLimitsArray(newValue) {
            this.limitsArray = [...newValue];
        },
        setDocumentsArray(newValue) {
            this.documentsArray = [...newValue];
        },
        setBonusesArray(newValue) {
            this.bonusesArray = [...newValue];
        },
        setDepositBonusesArray(newValue) {
            this.depositBonusesArray = [...newValue];
        },
        setDepositPaymentOptions(newValue) {
            this.depositPaymentOptions = [...newValue];
        },
        setWithdrawPaymentOptions(newValue) {
            this.withdrawPaymentOptions = [...newValue];
        },
        setPaymentInfoList(newValue) {
            this.paymentInfoList = [...newValue];
        },
        setUserCampaignsList(newValue) {
            this.userCampaignsList = [...newValue];
        },
        setVipProgressObject(newValue) {
            this.vipProgressObject = newValue ? Object.assign({}, newValue) : null;
        },
        setLiveGameStatsArray(newValue) {
            this.liveGameStatsArray = [...newValue];
        },
        setRecentWinningsArray(newValue) {
            this.recentWinningsArray = [...newValue];
        },
    },
    getters: {
        userCurrencyCode: (state) => {
            return state.realBalanceObject ? state.realBalanceObject.currencyCode : "";
        },
        userCurrency: (state) => {
            return state.realBalanceObject ? currencyCodeToSymbol[state.realBalanceObject.currencyCode] : "";
        },
        formattedRealBalance: (state) => {
            return `${state.realBalanceObject ? state.realBalanceObject.amount : "0.00"} ${state.realBalanceObject ? currencyCodeToSymbol[state.realBalanceObject.currencyCode] : ""}`;
        },
        formattedBonusBalance: (state) => {
            return `${state.bonusBalanceObject ? state.bonusBalanceObject.amount : "0.00"} ${state.realBalanceObject ? currencyCodeToSymbol[state.realBalanceObject.currencyCode] : ""}`;
        },
        formattedVirtualBalance: (state) => {
            return `${state.virtualBalanceObject ? Math.floor(state.virtualBalanceObject.amount) : "0"}`;
        },
        formattedTotalBalance: (state) => {
            const realBalance = state.realBalanceObject ? state.realBalanceObject.amount : "0.00";
            const bonusBalance = state.bonusBalanceObject ? state.bonusBalanceObject.amount : "0.00";
            const totalBalance = parseFloat(realBalance) + parseFloat(bonusBalance);
            if (state.realBalanceObject) {
                return `${totalBalance.toFixed(2)} ${currencyCodeToSymbol[state.realBalanceObject.currencyCode]}`;
            } else {
                return `${totalBalance.toFixed(2)}`;
            }
        },
        unFormattedRealBalance: (state) => {
            let realBalance = state.realBalanceObject ? state.realBalanceObject.amount : "0.00";
            realBalance = parseFloat(realBalance);
            return realBalance;
        },
        unFormattedTotalBalance: (state) => {
            const realBalance = state.realBalanceObject ? state.realBalanceObject.amount : "0.00";
            const bonusBalance = state.bonusBalanceObject ? state.bonusBalanceObject.amount : "0.00";
            const totalBalance = parseFloat(realBalance) + parseFloat(bonusBalance);
            return totalBalance.toFixed(2);
        },
        countryCode: (state) => {
            if (state.locationObject) {
                return state.locationObject.country.code.toLowerCase();
            }
            return null;
        },
        activeBonuses: (state) => {
            if (state.bonusesArray.length > 0) {
                let activeBonuses = [];
                state.bonusesArray.map((bonus) => {
                    if (bonus.node.unifiedStatus === "ACTIVE" || bonus.node.unifiedStatus === "PENDING") {
                        activeBonuses = [...activeBonuses, bonus.node];
                    }
                });
                return activeBonuses;
            }
            return [];
        },
        availableBonuses: (state) => {
            let availableBonuses = [];
            state.bonusesArray.map((bonus) => {
                if (bonus.node.unifiedStatus === "AVAILABLE") {
                    availableBonuses = [...availableBonuses, bonus.node];
                }
            });

            state.depositBonusesArray.map((depositBonus) => {
                let bonusAlreadyExist = false;
                availableBonuses.map((available) => {
                    if (available.bonusId === depositBonus.bonusId) {
                        bonusAlreadyExist = true;
                    }
                });
                if (bonusAlreadyExist === false) {
                    availableBonuses = [...availableBonuses, depositBonus];
                }
            });
            return availableBonuses;
        },
        pendingWithdrawals: (state) => {
            let pendingWithdrawals = [];
            state.paymentConnectionsArray.map((transaction) => {
                if (transaction.node.type === "WITHDRAWAL" && (transaction.node.status === "PENDING" || transaction.node.status === "PENDING_REDIRECT" || transaction.node.status === "PENDING_APPROVAL")) {
                    pendingWithdrawals.push(transaction);
                }
            });
            return pendingWithdrawals;
        },
        fullDepositOptions: (state) => {
            return fullPaymentOptions(state.depositPaymentOptions, state.paymentInfoList);
        },
        fullWithdrawOptions: (state) => {
            return fullPaymentOptions(state.withdrawPaymentOptions, state.paymentInfoList);
        },
    },
});

const fullPaymentOptions = (paymentOptions, paymentInfoList) => {
    let options = [];

    paymentInfoList.map((info) => {
        paymentOptions.some((payment) => {
            if (info.attributes.slug.indexOf(`${payment.method}_${payment.provider}`) !== -1) {
                const strapiInfo = {
                    name: info.attributes.name,
                    description: info.attributes.description,
                    slug: `${payment.method}_${payment.provider}`,
                };
                options = [...options, Object.assign(strapiInfo, payment)];
                return true;
            }
        });
    });
    return options;
};
